import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Share2, MapPin, ChevronLeft } from 'lucide-react';
import { MdShare } from 'react-icons/md';
import OpenGalleryModal from '../SingleRest/OpenGalleryModal';
import MapOverlay from '../../MapOverlay';
import RestaurantSection from '../Explore/Sect';
import { slugify, useUserActivity, useUserLocation } from '../Explore/services';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getAreaFeed, getAreaPhotos } from '../../../lib/api/feed.apis';
import type { Restaurant } from '../../Manager/restaurant.types';
import RestCard from '../Components/RestCard';
import { Helmet } from 'react-helmet';

interface ExploreArea {
  areaInfo: {
    name: string;
    city: string;
  };
  topRestaurants: Restaurant[];
  midRangeRestaurants: Restaurant[];
  areaPhotos: {
    url: string;
    restaurantName: string;
    restaurantId: string;
  }[]
};

// Restaurant Card Skeleton
const RestaurantCardSkeleton = () => (
  <div className="flex gap-3 p-2 bg-white rounded-xl shadow-sm animate-pulse">
    <div className="relative">
      <div className="absolute top-1 right-1 z-10">
        <div className="w-6 h-6 bg-gray-300 rounded-full animate-pulse"></div>
      </div>
      <div className="w-24 h-24 bg-gray-300 rounded-md flex-shrink-0 animate-pulse"></div>
    </div>
    <div className="flex-1 min-w-0 space-y-2">
      <div className="h-5 bg-gray-300 rounded w-3/4 animate-pulse"></div>
      <div className="h-4 bg-gray-300 rounded w-1/2 animate-pulse"></div>
      <div className="h-4 bg-gray-300 rounded w-2/3 animate-pulse"></div>
      <div className="h-3 bg-gray-300 rounded w-1/4 animate-pulse"></div>
    </div>
  </div>
);

// Main Area Page Component
const SingleArea = () => {
  const [exploreArea, setExploreArea] = useState<ExploreArea | null>(null);
  const { location, loading: locationLoading } = useUserLocation();
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { trackClick } = useUserActivity();
  const navigate = useNavigate();
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [loadingMore, setloadingMore] = useState(false);
  const [areaPhotos, setAreaPhotos] = useState(
    [{
    url: '',
    restaurantName: '',
    restaurantId: '',
  }])

  
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const areaName = searchParams.get('name');

  useEffect(() => {
    // Fetch area data based on slug or areaName
    const fetchAreaData = async () => {
      if (location) {
        setLoading(true);
        try {
          const response = await getAreaFeed(areaName || slug || 'Westlands', 0, 0);

          const raw = response.data
          setExploreArea({
            areaInfo: raw.areaInfo,
            topRestaurants: raw.topRestaurants,
            midRangeRestaurants: raw.midRangeRestaurants,
            areaPhotos: raw.ambiancePhotos,
          });

          addNewPhotos([...raw.ambiancePhotos])
          setPage(1);
          setHasMore(true);
          getPhotos()
        } catch (error) {
          console.error("Error fetching area data:", error);
        } finally {
          setLoading(false);
        }
      }
    }

    fetchAreaData();
  }, [location, areaName, slug]);

  const handleBack = () => {
    navigate('/');
  };

  const addNewPhotos = (raw) => {
    setAreaPhotos((prev) => {
      // Create a Set of existing photo identifiers (use url or restaurantId)
      const existingIds = new Set(prev.map(p => p.url)); // or p.restaurantId

      // Filter out duplicates
      const filteredNew = raw.filter(p => !existingIds.has(p.url));

      // Append only new ones
      return [...prev, ...filteredNew];
    });
  };

  const handleCardClick = useCallback((id: string, name: string) => {
    trackClick(id.toString());
    navigate(`/restaurant/${id || "1"}/${slugify(name)}`);
  }, [trackClick, navigate]);

  const openRestaurantsList = useCallback((areaName: string) => {
    navigate(`/areas/${slugify(areaName)}/restaurants?name=${encodeURIComponent(areaName)}`);
  }, [navigate]);

  const handleShare = () => {
    if (!exploreArea) return;
    
    if (navigator.share) {
      navigator.share({
        title: exploreArea.areaInfo.name,
        text: `Check out restaurants in ${exploreArea.areaInfo.name}`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const getPhotos = async () => {
    if (loadingMore || !hasMore) return;

    try {
      setloadingMore(true)
      const res = await getAreaPhotos(areaName, 5, page);

      const { hasMore, page: currentPage, photos} = res;

      setHasMore(hasMore);
      setPage(currentPage + 1);
      addNewPhotos(photos);
    } catch (error) {
      console.error(error);
    } finally {
      setloadingMore(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Sticky Header Skeleton */}
        <header className="sticky top-0 z-20 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between p-4">
            <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
            <div className="h-6 bg-gray-300 rounded w-32 animate-pulse"></div>
            <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
          </div>
        </header>

        {/* Photo Gallery Section Skeleton */}
        <section className="bg-white px-4">
          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex gap-4 py-4">
              {[1, 2, 3].map((index) => (
                <div key={index} className="flex-shrink-0 w-60 h-78 bg-gray-300 rounded-sm animate-pulse"></div>
              ))}
            </div>
          </div>
          <div className="px-1 pb-6">
            <div className="h-8 bg-gray-300 rounded w-48 animate-pulse mb-2"></div>
            <div className="h-4 bg-gray-300 rounded w-64 animate-pulse"></div>
          </div>
        </section>

        {/* Divider */}
        <div className="h-1 bg-gray-100"></div>

        {/* Top Restaurants Skeleton */}
        <section className="bg-white p-4">
          <div className="h-6 bg-gray-300 rounded w-32 mb-4 animate-pulse"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((index) => (
              <RestaurantCardSkeleton key={index} />
            ))}
          </div>
        </section>

        {/* Mid-Range Restaurants Skeleton */}
        <section className="bg-white p-4">
          <div className="h-6 bg-gray-300 rounded w-40 mb-4 animate-pulse"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((index) => (
              <RestaurantCardSkeleton key={index} />
            ))}
          </div>
        </section>

        {/* Floating Map Button Skeleton */}
        <div className="sticky bottom-0 left-0 right-0 z-40">
          <div className="bg-gradient-to-t from-black/60 via-black/20 to-transparent pt-12 pb-6 px-4">
            <div className="w-full bg-gray-300 h-14 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!exploreArea) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-500 mb-4">No area data found</div>
          <button 
            onClick={handleBack}
            className="px-6 py-2 bg-gray-900 text-white rounded-full font-medium"
          >
            Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{exploreArea.areaInfo.name} – Gemio</title>
        <meta
          name="description"
          content={`Discover and reserve the best restaurants in ${exploreArea.areaInfo.name} with Gemio. Browse menus, read reviews, and book a table at top dining spots in ${exploreArea.areaInfo.name}.`}
        />
      </Helmet>
      <div className="min-h-screen">
        {/* Sticky Header */}
        <header className="sticky top-17 z-9 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-4">
            <button className="p-2 active:bg-gray-100 rounded-full transition" onClick={handleBack}>
              <ChevronLeft size={24} className="text-gray-700" />
            </button>
            <h1 className="font-semibold text-gray-900">{exploreArea.areaInfo.name}</h1>
            <button className="p-2 active:bg-gray-100 rounded-full transition" onClick={handleShare}>
              <MdShare size={20} className="text-gray-700" />
            </button>
          </div>
        </header>

        {/* Photo Gallery Section */}
        <section className="bg-white px-4">
          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex gap-4 py-4">
              {exploreArea.areaPhotos.map((photo, index) => (
                <div key={index} className="relative flex-shrink-0 w-60 h-78 overflow-hidden rounded-sm">
                  <img 
                    src={photo.url} 
                    alt={photo.restaurantName}
                    className="w-full h-full object-cover rounded-sm"
                  />
                  <div className="absolute bottom-2 right-2 text-white text-xs bg-black/50 px-2 py-1 rounded">
                    {photo.restaurantName}
                  </div>
                  {index === exploreArea.areaPhotos.length - 1 && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-sm hover:bg-black/30 cursor-pointer transition">
                      <button className="bg-white text-gray-900 px-4 py-2 rounded-full font-medium text-sm shadow-lg hover:bg-gray-100 transition" onClick={() => setIsGalleryOpen(true)}>
                        View Full Gallery
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="px-1 pb-6">
            <h2 className="Logo-sans text-2xl font-bold text-gray-900">{exploreArea.areaInfo.name}</h2>
            <p className="text-gray-600">Restaurants in {exploreArea.areaInfo.city}</p>
          </div>
        </section>

        {/* Divider */}
        <div className="h-1 bg-gray-100"></div>

        {/* Top Restaurants */}
        <section className="bg-white px-1 py-2">
          <h2 className="text-lg pl-2 font-bold mid-sans text-[#bf491e]">Top Restaurants</h2>
          <div className="space-y-3">
            {exploreArea.topRestaurants.map(restaurant => (
              <RestCard key={restaurant._id} restaurant={restaurant} onCardClick={handleCardClick} />
            ))}
          </div>
        </section>

        <div className="w-10/12 mid-sans border rounded-full text-center p-3 my-4 text-gray-700 font-medium cursor-pointer hover:bg-gray-200 active:bg-gray-300 transition mx-auto" onClick={() => openRestaurantsList(exploreArea.areaInfo.name)}>
          View All Restaurants
        </div>

        {/* Mid-Range Restaurants */}
        <div className='px-4'>
          {exploreArea.midRangeRestaurants.length > 0 &&<RestaurantSection
            title={`Mid-range spots in ${exploreArea.areaInfo.name}`}
            restaurants={exploreArea.midRangeRestaurants}
            onCardClick={handleCardClick}
          />}
        </div>

        <OpenGalleryModal
          name={exploreArea.areaInfo.name}
          images={areaPhotos || exploreArea.areaPhotos}
          isGalleryOpen={isGalleryOpen}
          setIsGalleryOpen={setIsGalleryOpen}
          getMorePhotos={getPhotos}
          hasMore={hasMore}
          isLoadingMore={loadingMore}
          enableInfiniteScroll={true}
        />
      </div>
    </>
  );
};

export default SingleArea;